// Matches the server cap of batch-archive, batch-delete and batch-force-delete;
// the client sends larger selections as sequential chunks.
export const BATCH_POST_LIMIT = 500

// Matches the server cap of media-assets/batch-delete (maxBatchMediaAssetIDs).
export const BATCH_MEDIA_ASSET_LIMIT = 100

export function chunkIds(ids, size = BATCH_POST_LIMIT) {
  const chunks = []
  for (let index = 0; index < ids.length; index += size) chunks.push(ids.slice(index, index + size))
  return chunks
}

// Runs request once per batch, in order. When a later batch fails, the error
// carries the post ids already applied (processedPostIds) so callers can
// report and prune them; later batches are not sent.
export async function runBatches(batches, request, idsOf = (batch) => batch) {
  const processed = []
  for (const batch of batches) {
    try {
      await request(batch)
    } catch (error) {
      throw Object.assign(error, { processedPostIds: [...processed] })
    }
    processed.push(...idsOf(batch))
  }
  return processed
}

export function runInChunks(ids, request, size = BATCH_POST_LIMIT) {
  return runBatches(chunkIds(ids, size), request)
}

// Runs request for every item, at most `limit` at once, and resolves to the
// results in item order. After a rejection no further item starts and the
// run rejects with it; callers that keep per-item failures catch them in request.
export async function mapWithConcurrency(items, limit, request) {
  const results = new Array(items.length)
  let next = 0
  let failed = false
  const lane = async () => {
    while (!failed && next < items.length) {
      const index = next
      next += 1
      try {
        results[index] = await request(items[index], index)
      } catch (error) {
        failed = true
        throw error
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(Math.max(limit, 1), items.length) }, lane))
  return results
}

// Groups { postId, orgSlug } targets by book (first-seen order) and chunks
// each book's ids at `size`, so a selection spanning books is sent to each
// book's own batch endpoint.
export function batchTargetsByOrg(targets, size = BATCH_POST_LIMIT) {
  const byOrg = new Map()
  for (const target of targets) {
    if (!byOrg.has(target.orgSlug)) byOrg.set(target.orgSlug, [])
    byOrg.get(target.orgSlug).push(target.postId)
  }
  return [...byOrg].flatMap(([orgSlug, ids]) => chunkIds(ids, size).map((chunk) => ({ orgSlug, ids: chunk })))
}
